import React, { useState } from 'react';
import { Tooltip } from '@plerionhq/design-system';
import { FeedbackDialog } from './FeedbackDialog';
import { FeedbackType, useSubmitFeedback } from '../../../../api/feedback';
import { useChatStore } from '../../store/chatStore';
import toast from 'react-hot-toast';

import { useTranslation } from 'react-i18next';

interface FeedbackButtonsProps {
  messageId: string;
  conversationId: string;
  onFeedbackSubmit?: (success: boolean) => void;
}

export const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  messageId,
  conversationId,
  onFeedbackSubmit,
}) => {
  const { t } = useTranslation('pleri/chat');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [pendingFeedbackType, setPendingFeedbackType] =
    useState<FeedbackType | null>(null);

  const { mutateAsync: submitFeedback, isPending } = useSubmitFeedback();
  const setMessageFeedback = useChatStore((state) => state.setMessageFeedback);
  const feedback = useChatStore(
    (state) =>
      state.conversations[conversationId]?.messages.find(
        (m) => m.id === messageId,
      )?.feedback,
  );

  const handleFeedbackClick = (feedbackType: FeedbackType) => {
    if (isPending) {
      return;
    }

    // If clicking the same feedback type, deselect it
    if (feedback?.type === feedbackType) {
      setMessageFeedback(conversationId, messageId, null);
      return;
    }

    setPendingFeedbackType(feedbackType);
    setIsDialogOpen(true);
  };

  const handleSubmitFeedback = async (message?: string) => {
    if (!pendingFeedbackType) {
      return;
    }

    try {
      await submitFeedback({
        messageId,
        conversationId,
        feedbackType: pendingFeedbackType,
        message,
      });

      setMessageFeedback(conversationId, messageId, {
        type: pendingFeedbackType,
        message,
      });
      onFeedbackSubmit?.(true);
      toast.success(t('thankYouForYourFeedback'));
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error(t('failedToSubmitFeedbackPleaseTryAgain'));
      onFeedbackSubmit?.(false);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setPendingFeedbackType(null);
  };

  return (
    <>
      <div className="flex items-center gap-1">
        <Tooltip label={t('goodResponse')}>
          <button
            onClick={() => handleFeedbackClick('upvote')}
            className={`group rounded-md p-1.5 transition-all ${
              feedback?.type === 'upvote'
                ? 'text-[#FF732E]'
                : 'text-gray-400 hover:text-[#FF732E]/70 dark:text-gray-500'
            }`}
            disabled={isPending}
            aria-label={t('upvoteResponse')}
          >
            <svg
              className="h-4 w-4 transition-transform group-hover:scale-110"
              fill={'none'}
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23H5.904M14.25 9h2.25M5.904 18.75c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 01-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 10.203 4.167 9.75 5 9.75h1.053c.472 0 .745.556.5.96a8.958 8.958 0 00-1.302 4.665c0 1.194.232 2.333.654 3.375z"
              />
            </svg>
          </button>
        </Tooltip>

        <Tooltip label={t('badResponse')}>
          <button
            onClick={() => handleFeedbackClick('downvote')}
            className={`group rounded-md p-1.5 transition-all ${
              feedback?.type === 'downvote'
                ? 'text-[#FF732E]'
                : 'text-gray-400 hover:text-[#FF732E]/70 dark:text-gray-500'
            }`}
            disabled={isPending}
            aria-label={t('downvoteResponse')}
          >
            <svg
              className="h-4 w-4 transition-transform group-hover:scale-110"
              fill={'none'}
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M7.5 15h2.25m8.024-9.75c.011.05.028.1.052.148.591 1.2.924 2.55.924 3.977a8.96 8.96 0 01-.999 4.125m.023-8.25c-.076-.365.183-.75.575-.75h.908c.889 0 1.713.518 1.972 1.368.339 1.11.521 2.287.521 3.507 0 1.553-.295 3.036-.831 4.398C20.613 14.547 19.833 15 19 15h-1.053c-.472 0-.745-.556-.5-.96a8.95 8.95 0 00.303-.54m.023-8.25H16.48a4.5 4.5 0 01-1.423-.23l-3.114-1.04a4.5 4.5 0 00-1.423-.23H6.504c-.618 0-1.217.247-1.605.729A11.95 11.95 0 002.25 12c0 .434.023.863.068 1.285C2.427 14.306 3.346 15 4.372 15h3.126c.618 0 .991.724.725 1.282A7.471 7.471 0 007.5 19.5a2.25 2.25 0 002.25 2.25.75.75 0 00.75-.75v-.633c0-.573.11-1.14.322-1.672.304-.76.93-1.33 1.653-1.715a9.04 9.04 0 002.86-2.4c.498-.634 1.226-1.08 2.032-1.08h.384"
              />
            </svg>
          </button>
        </Tooltip>
      </div>
      <FeedbackDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        feedbackType={pendingFeedbackType || 'upvote'}
        onSubmit={handleSubmitFeedback}
      />
    </>
  );
};
